import { create } from 'zustand';

// Guided first-run coaching. Progress is DERIVED from the player's real state (see
// hooks/useOnboarding.ts), so the tutorial can never get stuck or block input: it only
// ever shows a small hint card, and the player may skip it at any time.
//
// State is persisted PER PLAYER (`playerId`), not just per browser: otherwise a new
// account signing in on a browser that previously hosted an experienced one (very common
// on CrazyGames, where each guest gets a fresh account on the same device) would inherit
// a 'done' flag and never see the tutorial.
//
// The `status` decision is taken ONCE per player, so levelling up mid-tutorial can't cut
// it short.

const KEY = 'onboarding_v2';

export type OnboardingStatus = 'unknown' | 'active' | 'done';

interface Saved {
  /** Account this record belongs to; a different id re-evaluates from scratch. */
  playerId: string | null;
  status: OnboardingStatus;
  /** Furthest step reached (never goes backwards). */
  step: number;
  /** First successful hack landed. */
  hacked: boolean;
  /** access.log deleted on a compromised node. */
  logDeleted: boolean;
  /** A node OTHER than the entry whois node was hacked. */
  hackedOther: boolean;
}

const EMPTY: Saved = {
  playerId: null, status: 'unknown', step: 0,
  hacked: false, logDeleted: false, hackedOther: false,
};

function load(): Saved {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Saved>;
      return {
        playerId:    typeof p.playerId === 'string' ? p.playerId : null,
        status:      p.status === 'active' || p.status === 'done' ? p.status : 'unknown',
        step:        typeof p.step === 'number' ? p.step : 0,
        hacked:      !!p.hacked,
        logDeleted:  !!p.logDeleted,
        hackedOther: !!p.hackedOther,
      };
    }
  } catch { /* corrupt/unavailable storage — start fresh */ }
  return { ...EMPTY };
}

function save(s: Saved) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* quota / private mode */ }
}

interface OnboardingStore extends Saved {
  /** Decide once, for THIS player, whether to run the tutorial. */
  decide: (playerId: string, isNewPlayer: boolean) => void;
  /** Record the furthest step reached (monotonic). */
  reachStep: (step: number) => void;
  notifyHackSuccess: (wasEntryNode: boolean) => void;
  notifyLogDeleted: () => void;
  /** Player pressed Skip, or finished the last step. */
  finish: () => void;
  /** Restart the tutorial (offered from the Profile page). */
  restart: () => void;
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => {
  /** Persist + apply a patch to the current record. */
  const patch = (p: Partial<Saved>) => {
    const next: Saved = { ...get(), ...p };
    // Strip the actions before saving (we only persist the data fields).
    save({
      playerId: next.playerId, status: next.status, step: next.step,
      hacked: next.hacked, logDeleted: next.logDeleted, hackedOther: next.hackedOther,
    });
    set(p);
  };

  return {
    ...load(),

    decide: (playerId: string, isNewPlayer: boolean) => {
      const cur = get();
      // Already decided for this same account — never re-evaluate.
      if (cur.playerId === playerId && cur.status !== 'unknown') return;
      patch({
        ...EMPTY,
        playerId,
        status: isNewPlayer ? 'active' : 'done',
      });
    },

    reachStep: (step: number) => {
      const cur = get();
      if (cur.status !== 'active' || step <= cur.step) return;
      patch({ step });
    },

    notifyHackSuccess: (wasEntryNode: boolean) => {
      const cur = get();
      const p: Partial<Saved> = {};
      if (!cur.hacked) p.hacked = true;
      if (!wasEntryNode && !cur.hackedOther) p.hackedOther = true;
      if (Object.keys(p).length) patch(p);
    },

    notifyLogDeleted: () => {
      if (get().logDeleted) return;
      patch({ logDeleted: true });
    },

    finish: () => patch({ status: 'done' }),

    restart: () => patch({ ...EMPTY, playerId: get().playerId, status: 'active' }),
  };
});
